import { parseCookies, setCookie } from "nookies";
import { createContext, useEffect, useRef, useState } from "react";
import { api } from "../services/apiClient";
import {
  SESSAO_EXPIRADA_EVENT,
  TOKEN_COOKIE,
  limparTokenTodosOsPaths,
  tokenDuplicado,
} from "../services/api";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleError } from "../services/erros/ErrorHandler";

export const AuthContext = createContext({});

function AuthProvider({ children }) {
  const [user, setUser] = useState();
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Evita mostrar o toast/logout mais de uma vez pro mesmo "incidente" de sessão expirada: várias
  // chamadas em paralelo podem receber 401 quase ao mesmo tempo (ex. duas requisições de uma mesma
  // tela), e cada uma dispara SESSAO_EXPIRADA_EVENT de forma independente. Resetado no próximo login.
  const sessaoExpiradaTratadaRef = useRef(false);

  useEffect(() => {
    async function loadUser() {
      // Autocura de navegador com cookie duplicado: se existe mais de uma cópia do token, uma
      // delas está escondida num path mais específico e vai ser a enviada nas rotas `/app/...`,
      // derrubando o usuário no primeiro clique do menu. Não dá pra saber pelo JS qual é a boa
      // (`parseCookies` só devolve a que vence), então apaga todas e pede um login limpo — custa
      // um login uma única vez e resolve de vez, sem o usuário ter que limpar nada no navegador.
      if (tokenDuplicado()) {
        limparTokenTodosOsPaths();
        setLoading(false);
        return;
      }

      const { [TOKEN_COOKIE]: token } = parseCookies();

      if (token) {
        api
          .get("/usuario/info")
          .then((response) => {
            const {
              id,
              cliente_id,
              nome,
              email,
              cliente,
              clientes = [],
              login,
              chamado_cliente_obrigatorio,
              notif_chamado_novo_para_ids,
              disponibilizar_area,
            } = response.data;

            let data = {
              id,
              cliente_id,
              nome,
              email,
              cliente,
              clientes,
              login,
              chamado_cliente_obrigatorio,
              notif_chamado_novo_para_ids,
              disponibilizar_area,
            };
            setUser(data);
          })
          .catch((error) => {
            handleError(error, "Erro ao fazer login direto");
            logout();
          });

        setLoading(false);
      }

      setLoading(false);
    }

    loadUser();
  }, []);

  useEffect(() => {
    function handleSessaoExpirada(evento) {
      if (sessaoExpiradaTratadaRef.current) return;
      sessaoExpiradaTratadaRef.current = true;

      // Antes qualquer 401 virava "expirou por inatividade" — inclusive logo após o login, o que
      // deixava a mensagem sem sentido pro usuário e mandava o suporte investigar inatividade que
      // nunca houve. Agora o motivo vem do back (ver middlewares/estaAutenticado.ts).
      const codigo = evento?.detail?.codigo;
      const mensagens = {
        sessao_expirada: "Sua sessão expirou por inatividade. Faça login novamente.",
        sessao_encerrada: "Sua sessão foi encerrada. Faça login novamente.",
        sessao_de_outro_usuario: "Sua sessão foi encerrada. Faça login novamente.",
      };

      toast.error(
        mensagens[codigo] || "Sua sessão não é mais válida. Faça login novamente.",
        { theme: "dark" }
      );
      logout();
    }

    window.addEventListener(SESSAO_EXPIRADA_EVENT, handleSessaoExpirada);
    return () =>
      window.removeEventListener(SESSAO_EXPIRADA_EVENT, handleSessaoExpirada);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sem isso, a sessão só é conferida quando alguma tela faz uma chamada de API por conta própria —
  // trocar de rota é 100% client-side, então encerrar a sessão pelo Raptor (ou ela expirar) só era
  // percebido na próxima ação que batesse no backend, podendo demorar bastante. Este ping periódico
  // detecta a revogação mesmo sem nenhuma ação do usuário; em caso de 401 o interceptor global
  // (services/api.js) já cuida do resto.
  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      api.get("/usuario/info").catch(() => {});
    }, 2 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [user]);

  async function signIn(user, pass) {
    setLoadingAuth(true);
    try {
      const response = await api.post("/usuario/login", {
        login: user,
        senha: pass,
      });

      const {
        id,
        cliente_id,
        nome,
        email,
        cliente,
        clientes = [],
        login,
        chamado_cliente_obrigatorio,
        notif_chamado_novo_para_ids,
        disponibilizar_area,
        token,
      } = response.data;

      // Apaga qualquer cópia velha ANTES de gravar a nova. Sem isso, uma cópia presa em
      // `/app/...` (gravada por uma versão antiga do Portal, sem `path`) continuava existindo
      // em paralelo e, por ser de path mais específico, era ela que o navegador mandava nas
      // rotas do menu — o login "funcionava" e a primeira navegação derrubava o usuário.
      limparTokenTodosOsPaths();

      setCookie(undefined, TOKEN_COOKIE, token, {
        maxAge: 60 * 60 * 24, // expira em 24h (sessão deslizante, renovada a cada request)
        path: "/", // todos caminhos tem acesso ao cookie
      });

      let data = {
        id,
        cliente_id,
        nome,
        email,
        cliente,
        clientes,
        login,
        chamado_cliente_obrigatorio,
        notif_chamado_novo_para_ids,
        disponibilizar_area,
      };
      setUser(data);
      sessaoExpiradaTratadaRef.current = false;

      api.defaults.headers["Authorization"] = `Bearer ${token}`;

      setLoadingAuth(false);

      toast.success("Sejá bem vindo ao Sistema!", {
        theme: "dark",
      });

      // navigate('/');
    } catch (error) {
      handleError(error, "Erro na requisição");
    }
  }

  async function logout() {
    try {
      // Encerra a sessão no servidor (marca `encerrado_em` em `atendimento_sessao`) — sem isso a
      // linha ficava "pendurada" até expirar sozinha (até 24h), acumulando uma por login em
      // Sistema → Usuários Logados > Atendimento no Raptor. Ignora falha: token já pode estar
      // expirado/inválido, e o logout no cliente precisa seguir adiante de qualquer forma.
      await api.post("/usuario/logout");
    } catch (error) {
      // intencional: ver comentário acima
    }
    // `path: "/"` é obrigatório: o cookie foi gravado com esse path (ver signIn), e o navegador
    // só apaga um cookie quando nome+path+domínio batem. Sem informar o path, o nookies apaga no
    // path *default da página atual* — que em `/app/chamados/123` é `/app/chamados`, não `/`. O
    // cookie com o token morto sobrevivia ao logout, o `loadUser()` do próximo carregamento o lia
    // de novo e caía em 401 → "sessão expirou" → logout, em loop. Ctrl+F5 não resolve (recarga não
    // mexe em cookie); só limpando os dados do site — que é o que os usuários vinham fazendo.
    limparTokenTodosOsPaths();
    // Não deixa o token morto na memória do client: `api` é um singleton criado no load do módulo,
    // então sem isso o próximo request continuaria mandando o Authorization antigo.
    delete api.defaults.headers["Authorization"];
    console.clear();
    //toast.error('fez logout do sistema');
    setUser(null);
    navigate("/");
  }

  return (
    <AuthContext.Provider
      value={{
        signed: !!user,
        user,
        signIn,
        logout,
        loadingAuth,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
